import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { OrderBook } from "@/components/landing/LiquidityBoard";
import { SCRIPS } from "@/data/scrips";
import {
  ArrowLeft, MapPin, Users, IndianRupee, TrendingUp, CalendarClock,
  Store, ShieldCheck, ArrowRight, ReceiptText, PieChart, Wallet,
} from "lucide-react";

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export default function ScripDetail() {
  const { scripId } = useParams();
  const s = SCRIPS.find((x) => x.scrip === scripId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [scripId]);

  if (!s) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar base="/" />
        <div className="mx-auto flex max-w-2xl flex-col items-center px-4 pb-24 pt-40 text-center" data-testid="scrip-not-found">
          <h1 className="font-heading text-3xl font-black text-slate-900">Scrip not found</h1>
          <p className="mt-3 text-slate-600">We couldn't find an opportunity with that scrip ID.</p>
          <Link to="/" className="clay-btn mt-8 rounded-full bg-orange-500 px-8 py-3.5 text-sm font-bold text-white" data-testid="scrip-notfound-home-btn">
            Back to all opportunities
          </Link>
        </div>
        <Footer base="/" />
      </div>
    );
  }

  const surplus = s.financials.monthlyRevenue - s.financials.monthlyExpenses;
  const investorPool = (surplus * s.financials.investorShare) / 100;
  const perFraction = investorPool / s.totalFractions;
  const maxPayout = Math.max(...s.payoutHistory.map((p) => p.amount));

  const fundingStats = [
    { label: "Raised so far", value: s.raised },
    { label: "Pool target (40%)", value: s.target },
    { label: "Investors", value: `${s.members} / 200` },
    { label: "Min. fraction", value: s.min },
    { label: "Est. IRR", value: s.irr },
    { label: "Tenure", value: s.tenure },
  ];

  const financialCards = [
    { icon: IndianRupee, label: "Monthly outlet revenue", value: inr(s.financials.monthlyRevenue) },
    { icon: ReceiptText, label: "Monthly operating expenses", value: inr(s.financials.monthlyExpenses) },
    { icon: Wallet, label: "Net operating surplus", value: inr(surplus) },
    { icon: PieChart, label: "Investor pool share", value: `${s.financials.investorShare}%` },
    { icon: TrendingUp, label: "Est. payout per fraction / month", value: inr(perFraction) },
    { icon: Users, label: "Total fractions issued", value: `${s.totalFractions}` },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900">
      <Navbar base="/" />
      <main className="pb-24 pt-32">
        <div className="mx-auto max-w-6xl px-4" data-testid="scrip-detail-page">
          <Link to="/#opportunities" data-testid="scrip-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 transition-colors hover:text-orange-600">
            <ArrowLeft className="h-4 w-4" /> All opportunities
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-slate-900 px-4 py-1.5 font-mono text-xs font-bold tracking-wider text-orange-400" data-testid="scrip-id-chip">
              {s.scrip}
            </span>
            <span className="rounded-full bg-emerald-100 px-4 py-1.5 text-xs font-bold text-emerald-700">{s.status}</span>
            <span className="rounded-full bg-orange-50 px-4 py-1.5 text-xs font-bold text-orange-600">{s.sector}</span>
            <span className="rounded-full bg-slate-100 px-4 py-1.5 text-xs font-bold text-slate-600" data-testid="scrip-instrument-chip">{s.instrument}</span>
          </div>

          <h1 className="font-heading mt-5 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl lg:text-5xl" data-testid="scrip-title">
            {s.title}
          </h1>
          <p className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-slate-500">
            <MapPin className="h-4 w-4 text-orange-500" /> {s.city} · {s.outletSize}
          </p>

          <div className="mt-10 grid items-stretch gap-7 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="overflow-hidden rounded-[2rem] shadow-xl shadow-slate-900/10">
              <img src={s.img} alt={s.title} className="h-full max-h-[420px] w-full object-cover" data-testid="scrip-image" />
            </div>

            <div className="clay-card flex flex-col rounded-[2rem] p-7" data-testid="scrip-funding-card">
              <div className="flex items-end justify-between text-sm">
                <span className="font-bold text-slate-700">{s.raised} raised</span>
                <span className="font-black text-orange-600" data-testid="scrip-funded-pct">{s.funded}% funded</span>
              </div>
              <div className="mt-2 h-3.5 overflow-hidden rounded-full bg-slate-200/80">
                <div className="progress-glow h-full rounded-full bg-gradient-to-r from-orange-400 to-orange-600" style={{ width: `${s.funded}%` }} />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {fundingStats.map((st) => (
                  <div key={st.label} className="rounded-2xl bg-orange-50/70 p-3 text-center">
                    <p className="font-heading text-sm font-extrabold text-slate-900">{st.value}</p>
                    <p className="mt-1 text-[10px] font-semibold text-slate-500">{st.label}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-3 rounded-2xl bg-slate-900 px-4 py-3 text-white">
                <Store className="h-5 w-5 shrink-0 text-orange-400" />
                <p className="text-xs leading-relaxed text-slate-300">
                  Operated by <span className="font-bold text-white">{s.operator}</span>, who holds
                  60% of the capital stack. Investors subscribe to the 40% pool via{" "}
                  <span className="font-bold text-white">{s.instrument}</span>, pre-vetted by
                  frantiger with monthly sales reports.
                </p>
              </div>
              <Link
                to="/#lead-form"
                data-testid="scrip-express-interest-btn"
                className="clay-btn mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 px-6 py-4 text-base font-bold text-white"
              >
                Express Interest in {s.scrip} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <section className="mt-16" data-testid="scrip-financials-section">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Outlet financials</p>
            <h2 className="font-heading mt-3 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
              Where the money flows each month
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {financialCards.map(({ icon: Icon, label, value }, i) => (
                <div key={label} className="clay-card flex items-center gap-4 rounded-3xl p-5" data-testid={`scrip-financial-${i + 1}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-heading text-xl font-black text-slate-900">{value}</p>
                    <p className="text-xs font-semibold text-slate-500">{label}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-3xl bg-white p-6 shadow-sm">
              <p className="text-xs font-bold text-slate-500">Monthly surplus split</p>
              <div className="mt-3 flex h-5 overflow-hidden rounded-full" data-testid="scrip-surplus-split">
                <div className="bg-slate-300" style={{ width: `${100 - s.financials.investorShare}%` }} />
                <div className="progress-glow bg-orange-500" style={{ width: `${s.financials.investorShare}%` }} />
              </div>
              <div className="mt-2 flex justify-between text-[11px] font-bold text-slate-500">
                <span>Operator & brand share · {100 - s.financials.investorShare}%</span>
                <span className="text-orange-600">Investor pool · {s.financials.investorShare}%</span>
              </div>
            </div>
          </section>

          <div className="mt-16 grid gap-7 lg:grid-cols-2">
            <section className="neu-card rounded-[2rem] p-7" data-testid="scrip-payout-history">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Payout history</p>
              <h3 className="font-heading mt-3 text-xl font-extrabold text-slate-900">Per fraction, per month</h3>
              <div className="mt-8 flex h-44 items-end gap-4">
                {s.payoutHistory.map((p) => (
                  <div key={p.month} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
                    <p className="text-xs font-extrabold text-slate-800">{inr(p.amount)}</p>
                    <div
                      className="w-full rounded-t-2xl bg-gradient-to-t from-orange-500 to-orange-400"
                      style={{ height: `${(p.amount / maxPayout) * 96}px` }}
                    />
                    <p className="text-[11px] font-bold text-slate-400">{p.month}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="neu-card rounded-[2rem] p-7" data-testid="scrip-pool-members">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Pool members</p>
              <h3 className="font-heading mt-3 text-xl font-extrabold text-slate-900">
                {s.members} of 200 seats filled
              </h3>
              <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-white/70">
                <div className="h-full rounded-full bg-gradient-to-r from-orange-400 to-orange-600" style={{ width: `${(s.members / 200) * 100}%` }} />
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {s.poolSample.map((m) => (
                  <span key={m.id} className="neu-badge rounded-full px-3.5 py-1.5 text-[11px] font-bold text-slate-600">
                    {m.id} · {m.fractions} fraction{m.fractions > 1 ? "s" : ""} · {m.city}
                  </span>
                ))}
                <span className="rounded-full bg-slate-900 px-3.5 py-1.5 text-[11px] font-bold text-orange-400">
                  + {s.members - s.poolSample.length} more members
                </span>
              </div>
              <p className="mt-5 flex items-center gap-2 text-[11px] font-semibold text-slate-400">
                <ShieldCheck className="h-4 w-4 text-emerald-500" /> Member identities are masked for privacy.
              </p>
            </section>
          </div>

          <section className="mt-16" data-testid="scrip-order-book-section">
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Offer to Buy · Offer to Sell</p>
                <h2 className="font-heading mt-3 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                  Open orders on {s.scrip}
                </h2>
              </div>
              <span className="glass-surface rounded-full px-4 py-2 text-xs font-bold text-slate-600" data-testid="scrip-trade-fee-note">Flat ₹100 fee / trade · Live demo board</span>
            </div>
            <div className="mt-8">
              <OrderBook buy={s.orders.buy} sell={s.orders.sell} testid="scrip-order-book" />
            </div>
          </section>

          <section className="mt-16 overflow-hidden rounded-[2rem] bg-slate-900 p-10 text-center" data-testid="scrip-cta-banner">
            <h2 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">
              Want a fraction of {s.title}?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-400">
              Join the fraction by frantiger waitlist and mention scrip{" "}
              <span className="font-mono font-bold text-orange-400">{s.scrip}</span> — our team will
              reserve your seat in the pool before it closes.
            </p>
            <Link
              to="/#lead-form"
              data-testid="scrip-cta-join-btn"
              className="clay-btn mt-7 inline-flex items-center gap-2 rounded-full bg-orange-500 px-8 py-4 text-base font-bold text-white"
            >
              Join the Investor Waitlist <ArrowRight className="h-4 w-4" />
            </Link>
          </section>

          <p className="mt-10 text-center text-[10px] leading-relaxed text-slate-400">
            All figures on this page are illustrative and simulated. This is not an offer, solicitation
            or invitation to invest. frantiger charges a 5% Annual Management Charge (AMC) to structure
            and manage the fractional raise, plus a flat ₹100 platform fee on each secondary Offer to
            Buy / Offer to Sell trade. Please review all documents and do independent due diligence
            before making any decision.
          </p>
        </div>
      </main>
      <Footer base="/" />
    </div>
  );
}
